import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, Modal, Input, message, Space, Empty } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, EyeOutlined } from '@ant-design/icons';
import SurveyService from '../../services/SurveyService';
import moment from 'moment';

const { TextArea } = Input;

export default function QoCSurveyReviewQueue() {
    const [surveys, setSurveys] = useState([]);
    const [loading, setLoading] = useState(false);
    const [approving, setApproving] = useState(null);

    const load = () => {
        setLoading(true);
        SurveyService.getPendingReview()
            .then(r => setSurveys(r.data?.data || []))
            .catch(e => message.error(e.response?.data?.message || 'Failed to load review queue'))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    const handleApprove = (record) => {
        Modal.confirm({
            title: `Approve "${record.title}"?`,
            content: 'This will publish a copy to the National Template Library. The original stays with its creator, unchanged.',
            okText: 'Approve & Publish',
            onOk: async () => {
                setApproving(record.id);
                try {
                    await SurveyService.approvePromotion(record.id);
                    message.success('Approved — now published to the National Template Library');
                    load();
                } catch (e) {
                    message.error(e.response?.data?.message || 'Approval failed');
                } finally { setApproving(null); }
            },
        });
    };

    const handleReject = (record) => {
        let notes = '';
        Modal.confirm({
            title: `Return "${record.title}" to owner?`,
            icon: null,
            content: (
                <div>
                    <div style={{ marginBottom: 8, fontSize: 12, color: '#888' }}>
                        Optional — let the owner know what to improve before resubmitting.
                    </div>
                    <TextArea rows={3} placeholder="Reason (optional)..."
                        onChange={e => { notes = e.target.value; }} />
                </div>
            ),
            okText: 'Return as Private',
            okButtonProps: { danger: true },
            onOk: async () => {
                try {
                    await SurveyService.rejectPromotion(record.id, notes);
                    message.success('Returned to owner as private');
                    load();
                } catch (e) {
                    message.error(e.response?.data?.message || 'Failed to reject');
                }
            },
        });
    };

    const columns = [
        {
            title: 'Survey', dataIndex: 'title',
            render: (title, r) => (
                <div>
                    <div style={{ fontWeight: 700, fontSize: 13 }}>{title}</div>
                    {r.category && <Tag style={{ fontSize: 10 }}>{r.category}</Tag>}
                    <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                        {(r.description || '').slice(0, 100)}
                    </div>
                </div>
            ),
        },
        { title: 'Submitted By', dataIndex: 'owner_name', width: 160 },
        {
            title: 'Submitted', dataIndex: 'submitted_for_review_at', width: 140,
            render: d => d ? moment(d).format('DD MMM YYYY') : '—',
        },
        {
            title: '', width: 200, render: (_, r) => (
                <Space>
                    <Button size="small" icon={<CheckCircleOutlined />} type="primary"
                        style={{ background: '#389e0d', borderColor: '#389e0d' }}
                        loading={approving === r.id}
                        onClick={() => handleApprove(r)}>
                        Approve
                    </Button>
                    <Button size="small" icon={<CloseCircleOutlined />} danger
                        onClick={() => handleReject(r)}>
                        Reject
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <div className="page-content">
            <div className="page-head d-flex">
                <div className="my-auto">
                    <h4 style={{ margin: 0 }}>Survey Review Queue</h4>
                    <small className="text-muted">
                        Tools submitted by regions for promotion to the National Template Library
                    </small>
                </div>
            </div>
            <div className="page-pad">
                <Card size="small">
                    {surveys.length === 0 && !loading ? (
                        <Empty description="Nothing pending review right now" />
                    ) : (
                        <Table columns={columns} dataSource={surveys} rowKey="id" loading={loading}
                            pagination={{ pageSize: 15 }} />
                    )}
                </Card>
            </div>
        </div>
    );
}
